import { ReactNode } from 'react';
import {
  LayoutDashboard,
  Users,
  Calendar,
  DollarSign,
  Mail,
  Settings,
  LogOut,
  Menu,
  X,
  Upload,
  Globe,
  Image,
  BookOpen,
  Star,
  GraduationCap,
  Landmark,
} from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../lib/auth';
import { cn } from '../lib/cn';

interface DashboardLayoutProps {
  children: ReactNode;
  currentPage: string;
  onNavigate: (page: string) => void;
}

const navigation = [
  { name: 'Dashboard', id: 'dashboard', icon: LayoutDashboard },
  { name: 'Members', id: 'members', icon: Users },
  { name: 'Events', id: 'events', icon: Calendar },
  { name: 'Finances', id: 'finances', icon: DollarSign },
  { name: 'Communications', id: 'communications', icon: Mail },
  { name: 'Website', id: 'website', icon: Globe },
  { name: 'Media', id: 'media', icon: Image },
  { name: 'Gabbai', id: 'gabbai', icon: BookOpen },
  { name: 'High Holidays', id: 'high-holidays', icon: Star },
  { name: 'School', id: 'school', icon: GraduationCap },
  { name: 'Cemetery', id: 'cemetery', icon: Landmark },
  { name: 'Import', id: 'import', icon: Upload },
  { name: 'Settings', id: 'settings', icon: Settings },
];

export function DashboardLayout({ children, currentPage, onNavigate }: DashboardLayoutProps) {
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out lg:hidden',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <img src="/logo-horizontal.svg" alt="OpenShul" className="h-8" />
          <button onClick={() => setSidebarOpen(false)} className="p-2">
            <X className="h-5 w-5" />
          </button>
        </div>
        <nav className="mt-4 px-2">
          {navigation.map((item) => (
            <button
              key={item.id}
              onClick={() => {
                onNavigate(item.id);
                setSidebarOpen(false);
              }}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium mb-1',
                currentPage === item.id
                  ? 'bg-primary-50 text-primary-700'
                  : 'text-gray-700 hover:bg-gray-100'
              )}
            >
              <item.icon className="h-5 w-5" />
              {item.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-1 bg-white border-r">
          <div className="flex items-center h-16 px-4 border-b">
            <img src="/logo-horizontal.svg" alt="OpenShul" className="h-8" />
          </div>
          <nav className="flex-1 mt-4 px-2">
            {navigation.map((item) => (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium mb-1',
                  currentPage === item.id
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </button>
            ))}
          </nav>
          <div className="p-4 border-t">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-medium">
                {user?.member?.firstName?.[0] || user?.email?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">
                  {user?.member ? `${user.member.firstName} ${user.member.lastName}` : user?.email}
                </p>
                <p className="text-xs text-gray-500 truncate">{user?.organization?.name}</p>
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-30 flex items-center h-16 px-4 bg-white border-b lg:hidden">
          <button onClick={() => setSidebarOpen(true)} className="p-2 -ml-2">
            <Menu className="h-5 w-5" />
          </button>
          <img src="/logo-horizontal.svg" alt="OpenShul" className="ml-2 h-7" />
        </div>

        {/* Page content */}
        <main className="p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
